import { useState, useEffect, useRef, useMemo } from 'react'
import { getImageUrl, getImageSrcSet, getImageSizes } from '../utils/imageUrl'

// Кеш для проверки поддержки форматов (определяется один раз)
let formatSupportCache = {
  webp: null,
  avif: null
}

const checkWebPSupport = () => {
  if (formatSupportCache.webp !== null) return formatSupportCache.webp
  if (typeof window === 'undefined') return false
  try {
    const canvas = document.createElement('canvas')
    formatSupportCache.webp = canvas.toDataURL('image/webp').indexOf('data:image/webp') === 0
    return formatSupportCache.webp
  } catch {
    formatSupportCache.webp = false
    return false
  }
}

const checkAVIFSupport = () => {
  if (formatSupportCache.avif !== null) return formatSupportCache.avif
  if (typeof window === 'undefined') return false
  try {
    const img = new Image()
    formatSupportCache.avif = 'avif' in img
    return formatSupportCache.avif
  } catch {
    formatSupportCache.avif = false
    return false
  }
}

/**
 * Оптимизированный компонент изображения с поддержкой:
 * - Responsive images (srcset, sizes)
 * - Lazy loading с Intersection Observer
 * - WebP/AVIF форматы
 * - Placeholder/blur эффект
 * - Адаптивные размеры
 */
const OptimizedImage = ({
  src,
  alt,
  className = '',
  style = {},
  width,
  height,
  sizes = '100vw',
  priority = false, // Для критичных изображений (above the fold)
  aspectRatio,
  objectFit = 'cover',
  placeholder = true,
  ...props
}) => {
  const [isLoaded, setIsLoaded] = useState(false)
  const [isInView, setIsInView] = useState(priority)
  const [hasError, setHasError] = useState(false)
  const imgRef = useRef(null)
  const observerRef = useRef(null)

  // Для priority изображений сразу загружаем
  useEffect(() => {
    if (priority && src) {
      setIsInView(true)
    }
  }, [priority, src])

  useEffect(() => {
    // Если priority, сразу показываем
    if (priority) {
      setIsInView(true)
      return
    }

    // Инициализация Intersection Observer после монтирования компонента
    // Используем requestAnimationFrame для избежания layout thrashing
    let rafId = null
    let timeoutId = null

    const initObserver = () => {
      if (!imgRef.current) {
        setIsInView(true)
        return
      }

      // Проверяем поддержку Intersection Observer
      if ('IntersectionObserver' in window) {
        try {
          observerRef.current = new IntersectionObserver(
            (entries) => {
              entries.forEach((entry) => {
                if (entry.isIntersecting) {
                  setIsInView(true)
                  if (observerRef.current && imgRef.current) {
                    observerRef.current.unobserve(imgRef.current)
                  }
                }
              })
            },
            {
              rootMargin: '50px',
              threshold: 0.01
            }
          )

          if (imgRef.current) {
            observerRef.current.observe(imgRef.current)
          }
        } catch (e) {
          // В случае ошибки просто показываем изображение
          setIsInView(true)
        }
      } else {
        // Fallback для старых браузеров
        setIsInView(true)
      }
    }

    // Используем requestAnimationFrame для отложенной инициализации
    // Это предотвращает "Layout was forced before the page was fully loaded"
    if (typeof requestAnimationFrame !== 'undefined') {
      rafId = requestAnimationFrame(() => {
        timeoutId = setTimeout(initObserver, 0)
      })
    } else {
      timeoutId = setTimeout(initObserver, 0)
    }

    return () => {
      if (rafId !== null && typeof cancelAnimationFrame !== 'undefined') {
        cancelAnimationFrame(rafId)
      }
      if (timeoutId !== null) {
        clearTimeout(timeoutId)
      }
      if (observerRef.current && imgRef.current) {
        try {
          observerRef.current.unobserve(imgRef.current)
        } catch (e) {
          // Игнорируем ошибки при cleanup
        }
      }
    }
  }, [priority])

  const handleLoad = () => {
    setIsLoaded(true)
  }

  const handleError = () => {
    setHasError(true)
    setIsLoaded(true)
  }

  if (!src) {
    return (
      <div
        className={`optimized-image-placeholder ${className}`}
        style={{
          backgroundColor: '#f5f5f5',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          ...style
        }}
      >
        <span style={{ color: '#999', fontSize: '14px' }}>No Image</span>
      </div>
    )
  }

  if (hasError) {
    return (
      <div
        className={`optimized-image-error ${className}`}
        style={{
          backgroundColor: '#f5f5f5',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          ...style
        }}
      >
        <span style={{ color: '#999', fontSize: '14px' }}>Failed to load</span>
      </div>
    )
  }

  // Генерируем URL с учетом параметров
  // Используем оригинальный URL без форсирования WebP, чтобы избежать 404
  const baseUrl = getImageUrl(src, width, null) // Не форсируем формат
  const srcSetJpeg = getImageSrcSet(src, [400, 600, 800, 1200, 1600], false) // JPEG srcset
  const imageSizes = getImageSizes(sizes)

  // Определяем оптимальный формат (с кешированием)
  const supportsWebP = useMemo(() => checkWebPSupport(), [])
  const supportsAVIF = useMemo(() => checkAVIFSupport(), [])

  // Создаем picture элемент для поддержки разных форматов
  // WebP не используется в srcset, так как файлы могут не существовать для всех размеров
  // Вместо этого используем только JPEG, который гарантированно существует
  const pictureContent = (
    <picture>
      {/* Используем только JPEG, так как WebP файлы могут не существовать */}
      {/* WebP будет использоваться автоматически бэкендом, если файл существует */}
      <img
        ref={imgRef}
        src={isInView ? baseUrl : undefined}
        srcSet={isInView && srcSetJpeg ? srcSetJpeg : undefined}
        sizes={imageSizes}
        alt={alt || ''}
        className={`optimized-image ${className} ${isLoaded ? 'loaded' : 'loading'}`}
        style={{
          width: '100%',
          height: aspectRatio ? 'auto' : height || '100%',
          objectFit,
          // Если placeholder отключен, показываем изображение сразу без fade-in
          transition: placeholder ? 'opacity 0.3s ease-in-out' : 'none',
          opacity: placeholder ? (isLoaded ? 1 : 0) : 1,
          ...style
        }}
        loading={priority ? 'eager' : 'lazy'}
        decoding="async"
        onLoad={handleLoad}
        onError={handleError}
        {...props}
      />
    </picture>
  )

  // Контейнер с placeholder
  const containerStyle = {
    position: 'relative',
    width: '100%',
    ...(aspectRatio && { aspectRatio }),
    ...(width && height && !aspectRatio && { aspectRatio: `${width}/${height}` }),
    // Убираем серый фон, если placeholder отключен
    ...(placeholder ? {} : { backgroundColor: 'transparent' }),
    ...style
  }

  // Если placeholder отключен, не показываем серый блок вообще
  if (!placeholder) {
    return (
      <div
        className={`optimized-image-container ${className}`}
        style={containerStyle}
      >
        {pictureContent}
      </div>
    )
  }

  // Placeholder включен - показываем shimmer эффект только пока не загружено
  if (!isLoaded) {
    return (
      <div
        className={`optimized-image-container ${className}`}
        style={{
          ...containerStyle,
          backgroundColor: '#f5f5f5',
          overflow: 'hidden',
        }}
      >
        {/* Blur placeholder */}
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
            backgroundSize: '200% 100%',
            animation: 'shimmer 1.5s infinite',
          }}
        />
        {pictureContent}
        <style>{`
          @keyframes shimmer {
            0% { background-position: -200% 0; }
            100% { background-position: 200% 0; }
          }
        `}</style>
      </div>
    )
  }

  return (
    <div
      className={`optimized-image-container ${className}`}
      style={containerStyle}
    >
      {pictureContent}
    </div>
  )
}

export default OptimizedImage

